import { trackMiscAction, trackVisualEditAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import ShareFrameModal from 'components/Modal/Share/ShareFrameModal';
import InspectorLeftPanel from 'components/Panel/InspectorLeftPanel';
import InspectorRightPanel from 'components/Panel/InspectorRightPanel';
import Button, { ButtonType } from 'components/Widget/Button/Button';
import DropdownMenu from 'components/Widget/DropdownMenu';
import ExportDropdown from 'components/Widget/ExportDropdown';
import ExportMenuItem from 'components/Widget/ExportMenuItem';
import FrameBar from 'components/Widget/FrameBar';
import {
	Navbar,
	NavbarButton,
	NavbarCenter,
	NavbarLeft,
	NavbarRight,
	NavbarTitle,
	ToolButton,
} from 'components/Widget/Navbar';
import { Tooltip } from 'components/Widget/Tooltip';
import { SelectionInfo, Tool } from 'dragonmouse-shared/devigner';
import eventManager from 'events/EventManager';
import { Point } from 'dragonmouse-shared/geometry';
import { GetSiteDesignSystemInfoResponse } from 'dragonmouse-shared/api/site';
import { DEFAULT_DEVICES, Device } from 'dragonmouse-shared/interfaces';
import { getActiveVisualEditsHash } from 'dragonmouse-shared/util';
import { MenuItemAction } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrame } from 'redux/db/reducers';
import { getFrameCenter, getFrameDisplayBounds, getSelectionSvg } from 'redux/db/util';
import { Pick } from 'redux/mode/classes';
import {
	centerCanvasAroundSceneBoundingBox,
	centerCanvasAroundScenePosition,
	setHovered,
	setInspectorLeftPanelDocked,
	setInspectorRightPanelDocked,
	setPrototypeScaling,
	setSelected,
	setSelectedDomElements,
	setSidebarVisible,
	showExportFramesAiModal,
	showExportFramesAssetsModal,
	showExportFramesCodeModal,
} from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import { frameSiteUrl } from 'utils';
import Prototype from './Prototype';
import * as S from './styles';

interface FrameInspectorProps {
	frameId: string;
}

const FrameInspector = ({ frameId }: FrameInspectorProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [showShareModal, setShowShareModal] = useState(false);
	const [activeTool, setActiveTool] = useState<Tool | null>(null);
	const [iframeReady, setIframeReady] = useState(false);
	const [contextMenuOpen, setContextMenuOpen] = useState(false);
	const [selectionInfo, setSelectionInfo] = useState<SelectionInfo | null>(null);
	const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(
		null
	);
	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [prototypeFrameSize, setPrototypeFrameSize] = useState({ width: 0, height: 0 });
	const [iframeReloadKey, setIframeReloadKey] = useState(0);
	const [dsInfo, setDsInfo] = useState<GetSiteDesignSystemInfoResponse | null>(null);
	const [activeMode, setActiveMode] = useState<string | null>(null);
	const [isEditingNavTitle, setIsEditingNavTitle] = useState(false);
	const [editedNavTitle, setEditedNavTitle] = useState('');
	const navTitleInputRef = useRef<HTMLInputElement>(null);

	// Pinned to the frameId the user entered on — arrow nav in prototype mode re-renders
	// without remount, so exit() uses this to skip reposition if user never navigated away.
	const entryFrameIdRef = useRef(frameId);

	// Hover state for panels
	const [leftPanelHovered, setLeftPanelHovered] = useState(false);
	const [rightPanelHovered, setRightPanelHovered] = useState(false);
	const leftHoverTimerRef = useRef<number | null>(null);
	const rightHoverTimerRef = useRef<number | null>(null);

	const {
		scene,
		frame,
		prototypeScaling,
		isDragging,
		inspectorLeftPanelDocked,
		inspectorRightPanelDocked,
		selectedDomElements,
		centeredFrameId,
	} = useSelector((r: RootReducer) => {
		return {
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			frame: selectFrame(r.db, frameId),
			prototypeScaling: r.mode.prototypeScaling,
			isDragging: r.mode.isDragging,
			inspectorLeftPanelDocked: r.mode.inspectorLeftPanelDocked,
			inspectorRightPanelDocked: r.mode.inspectorRightPanelDocked,
			selectedDomElements: r.mode.selectedDomElements,
			centeredFrameId: r.mode.centeredFrameId,
		};
	}, shallowEqual);

	const isElementSelected = selectedDomElements.length > 0 && !!selectionInfo;
	const leftPanelVisible = inspectorLeftPanelDocked || leftPanelHovered || isElementSelected;
	const rightPanelVisible = inspectorRightPanelDocked || rightPanelHovered || isElementSelected;
	const siteUrl = frame ? frameSiteUrl(frame) : null;

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	const onNavTitleClick = () => {
		if (!frame) return;
		setEditedNavTitle(frame.title ?? '');
		setIsEditingNavTitle(true);
		setTimeout(() => navTitleInputRef.current?.select(), 0);
	};

	const onNavTitleCommit = () => {
		if (!frame) return;
		const trimmed = editedNavTitle.trim();
		if (trimmed && trimmed !== (frame.title ?? '')) {
			forge.updateFrame({ update: { id: frame.id, title: trimmed } });
		}
		setIsEditingNavTitle(false);
	};

	const onNavTitleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') onNavTitleCommit();
		else if (e.key === 'Escape') setIsEditingNavTitle(false);
	};

	const exit = () => {
		if (!scene) {
			console.error('FrameInspector: cannot access scene');
			return;
		}

		if (frame && frame.id !== entryFrameIdRef.current) {
			dispatch(setSelected(new Pick(frame.id)));
			dispatch(setHovered(null));

			if (centeredFrameId) {
				// Entered from focus mode — re-focus on the frame we ended on.
				dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox: getFrameDisplayBounds(frame),
						animated: false,
						frameId: frame.id,
					})
				);
			} else {
				// Entered from the canvas — preserve zoom, pan so the frame's center
				// sits at canvas center.
				dispatch(
					centerCanvasAroundScenePosition({
						position: getFrameCenter(frame),
						animated: false,
					})
				);
			}
		}

		dispatch(setSidebarVisible(false));
		navigate(routes.scene(scene.id, scene.title));
	};

	const openInNewTab = useCallback(() => {
		if (!siteUrl) return;

		const url = new URL(siteUrl);

		// add veHash if visual edits are active
		const veHash = frame ? getActiveVisualEditsHash(frame) : null;

		if (veHash) {
			url.searchParams.append('veHash', veHash);
		}
		window.open(url.toString(), '_blank', 'noopener,noreferrer');
	}, [siteUrl, frame]);

	const onDevicePreset = (device: Device) => {
		setPrototypeFrameSize({ width: device.width, height: device.height });
	};

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		// here's a cleanup function to reset selected element ids when component unmounts
		return () => {
			dispatch(setSelectedDomElements([]));
		};
	}, [dispatch]);

	// Reset prototype frame size to the current frame's native dimensions whenever
	// the selected frame changes. Depending only on width/height would miss the
	// case where two frames share dimensions but a device preset (or drag-resize
	// via onSetFrameSize) has pushed prototypeFrameSize off-native — switching to
	// a same-sized sibling would silently inherit the old preset. Keying on
	// frame.id snaps the preview back to native on every frame switch.
	useEffect(() => {
		if (frame) {
			setPrototypeFrameSize({ width: frame.width, height: frame.height });
		}
	}, [frame?.id, frame?.width, frame?.height]);

	// Sync active mode from frame metadata
	useEffect(() => {
		setActiveMode(frame?.metadata?.mode ?? null);
	}, [frame?.metadata?.mode]);

	// Fetch DS info for theme toggle
	useEffect(() => {
		if (!frame?.siteId) {
			setDsInfo(null);
			return;
		}
		let cancelled = false;
		forge
			.getSiteDesignSystemInfo({ siteId: frame.siteId })
			.then((data: GetSiteDesignSystemInfoResponse) => {
				if (!cancelled) setDsInfo(data);
			})
			.catch(() => {
				if (!cancelled) setDsInfo(null);
			});
		return () => {
			cancelled = true;
		};
	}, [frame?.siteId]);

	// Reconcile active mode with available DS modes
	const modePickerData = useMemo(() => {
		const modes = dsInfo?.dsModes;
		if (!modes || modes.length < 2) return null;
		const defaultModeId = modes.find((dt) => dt.default)?.id ?? modes[0]?.id;
		const currentMode =
			activeMode && modes.some((dt) => dt.id === activeMode) ? activeMode : defaultModeId ?? null;
		const triggerColor =
			modes.find((t) => t.id === currentMode)?.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT;
		const options = modes.map((t) => ({
			id: t.id,
			color: t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT,
			label: t.label,
		}));
		return { options, currentMode, triggerColor };
	}, [dsInfo?.dsModes, activeMode]);

	const onSelection = (info: SelectionInfo) => {
		dispatch(setSelectedDomElements([info.element]));
		setSelectionInfo(info);
	};

	//------------------------------------------------------------------------------------------------
	// HOVER ZONE MANAGEMENT
	//------------------------------------------------------------------------------------------------

	const onLeftHoverEnter = useCallback(() => {
		if (leftHoverTimerRef.current) clearTimeout(leftHoverTimerRef.current);
		leftHoverTimerRef.current = window.setTimeout(() => {
			setLeftPanelHovered(true);
		}, 200);
	}, []);

	const onLeftHoverLeave = useCallback(() => {
		if (leftHoverTimerRef.current) clearTimeout(leftHoverTimerRef.current);
		leftHoverTimerRef.current = window.setTimeout(() => {
			setLeftPanelHovered(false);
		}, 200);
	}, []);

	const onRightHoverEnter = useCallback(() => {
		if (rightHoverTimerRef.current) clearTimeout(rightHoverTimerRef.current);
		rightHoverTimerRef.current = window.setTimeout(() => {
			setRightPanelHovered(true);
		}, 200);
	}, []);

	const onRightHoverLeave = useCallback(() => {
		if (rightHoverTimerRef.current) clearTimeout(rightHoverTimerRef.current);
		rightHoverTimerRef.current = window.setTimeout(() => {
			setRightPanelHovered(false);
		}, 200);
	}, []);

	// Clean up hover timers on unmount
	useEffect(() => {
		return () => {
			if (leftHoverTimerRef.current) clearTimeout(leftHoverTimerRef.current);
			if (rightHoverTimerRef.current) clearTimeout(rightHoverTimerRef.current);
		};
	}, []);

	//------------------------------------------------------------------------------------------------
	// NAVIGATION LOGIC
	//------------------------------------------------------------------------------------------------

	// Handle global Escape key to clear selection and close context menu
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				eventManager.emit('devigner-clear-selection', undefined);
				dispatch(setSelectedDomElements([]));
				setContextMenuOpen(false);
				setSelectionInfo(null);
			}
		};

		window.addEventListener('keydown', handleKeyDown, true);
		return () => window.removeEventListener('keydown', handleKeyDown, true);
	}, [dispatch]);

	const moreMenuItems = useMemo(() => {
		if (!frame) return [];

		return [
			new MenuItemAction(
				fullscreenMode ? 'Switch to fixed aspect ratio view' : 'Switch to fullscreen view',
				'',
				() => setFullscreenMode(!fullscreenMode),
				!siteUrl,
				fullscreenMode
			),
			new MenuItemAction(
				prototypeScaling === 'actual-size' ? 'Switch to mockup view' : 'Switch to actual size view',
				'',
				() =>
					dispatch(
						setPrototypeScaling(prototypeScaling === 'actual-size' ? 'mockup' : 'actual-size')
					),
				false,
				prototypeScaling === 'actual-size'
			),
			new MenuItemAction('Open in new tab', '', openInNewTab, !siteUrl),
		];
	}, [frame, fullscreenMode, prototypeScaling, siteUrl, dispatch, openInNewTab]);

	//------------------------------------------------------------------------------------------------
	// EARLY EXIT (must be defined after all hooks)
	//------------------------------------------------------------------------------------------------

	if (!frame) return null;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onPrototypeAreaClick = (e: React.MouseEvent) => {
		// Clicks inside the iframe don't bubble to React, so any click that reaches
		// here is on the background area. Don't deselect if clicking on an overlay panel.
		const target = e.target as HTMLElement;
		if (target.closest('[data-panel]')) return;
		eventManager.emit('devigner-clear-selection', undefined);
		dispatch(setSelectedDomElements([]));
		setSelectionInfo(null);
		setContextMenuOpen(false);
	};

	const onContextMenu = (selectionInfo: SelectionInfo, point: Point) => {
		if (!frame) return;
		// Use the position data that's already calculated in the Prototype component
		setContextMenuPosition(point);
		setSelectionInfo(selectionInfo);
		setContextMenuOpen(true);
	};

	const onExportClick = async () => {
		if (!selectionInfo || !frame?.siteId) return;

		try {
			const svgBlob = await getSelectionSvg(selectionInfo, frame.siteId);
			if (svgBlob) {
				const svgText = await svgBlob.text();
				await navigator.clipboard.writeText(svgText);
				toast.success('SVG copied to clipboard');
			}
		} catch (error) {
			toast.error('Failed to copy SVG');
		}
		setContextMenuOpen(false);
		setSelectionInfo(null);
	};

	// If a visual-edit entry already exists for the current siteId, flip the frame into
	// visualEditActive=true. Called when activating any editing tool so the tool always
	// operates against the visual-edits layer (never the bare site entry). No-op if there
	// are no visual edits yet — first real edit still creates the entry and promotes.
	// (this is done because clicking with the text edit tool on the non-visual-edit version of the site leads to confusion)
	const activateVisualEditsIfAny = () => {
		if (!frame || !frame.siteId) return;
		if (frame.metadata?.visualEditActive) return;
		const hasVisualEdits = frame.entries?.some(
			(e) => e.kind === 'visualEdit' && 'siteId' in e && e.siteId === frame.siteId
		);
		if (!hasVisualEdits) return;
		forge.updateFrame(
			{
				update: {
					id: frame.id,
					metadata: { ...frame.metadata, visualEditActive: true },
				},
			},
			true
		);
	};

	// Called when the user clicks a bare site entry in the left panel. The
	// previous selection is on a node that no longer exists in the new iframe
	// document, so clear all selection state — Redux ids, local selectionInfo,
	// the iframe highlight, and any open context menu. If they were mid
	// text/image tool, that tool can no longer operate against a bare site —
	// reset them to the select tool.
	const onSiteEntrySelected = () => {
		eventManager.emit('devigner-clear-selection', undefined);
		dispatch(setSelectedDomElements([]));
		setSelectionInfo(null);
		setContextMenuOpen(false);
		if (activeTool === 'text' || activeTool === 'image') {
			setActiveTool('select');
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderCenterControls = () => {
		if (!siteUrl) {
			return (
				<>
					<Tooltip placement="bottom" id="preview-tooltip" />
					<S.SelectButton
						active={true}
						disabled={!iframeReady || !siteUrl}
						onClick={() => {}}
						data-tooltip-id="preview-tooltip"
						data-tooltip-content={'Imported images can be previewed, but not edited'}
					>
						<Icon type={IconType.PLAY} width={16} height={16} />
						Preview
					</S.SelectButton>
				</>
			);
		}

		return (
			<>
				<ToolButton
					icon={IconType.PLAY}
					active={activeTool === null}
					onClick={() => {
						setActiveTool(null);
						dispatch(setSelectedDomElements([]));
					}}
					tooltip={iframeReady ? 'Preview' : 'Prototype code is loading, please wait...'}
					tooltipId="preview-tooltip"
				/>
				<S.ToolButtonSpacer />
				<ToolButton
					icon={IconType.SELECT}
					active={activeTool === 'select'}
					disabled={!iframeReady}
					onClick={() => {
						if (activeTool !== 'select') {
							trackVisualEditAction('activate_select_tool');
						}
						setActiveTool('select');
						dispatch(setSelectedDomElements([]));
					}}
					tooltip={iframeReady ? 'Select tool' : 'Prototype code is loading, please wait...'}
					tooltipId="select-tooltip"
				/>
				<ToolButton
					icon={IconType.TEXT_2}
					active={activeTool === 'text'}
					disabled={!iframeReady}
					onClick={() => {
						if (activeTool !== 'text') {
							trackVisualEditAction('activate_text_tool');
						}
						setActiveTool('text');
						dispatch(setSelectedDomElements([]));
						activateVisualEditsIfAny();
					}}
					tooltip={iframeReady ? 'Text tool' : 'Prototype code is loading, please wait...'}
					tooltipId="text-edit-tooltip"
				/>
				<ToolButton
					icon={IconType.IMAGE}
					active={activeTool === 'image'}
					disabled={!iframeReady}
					onClick={() => {
						if (activeTool !== 'image') {
							trackVisualEditAction('activate_image_tool');
						}
						setActiveTool('image');
						dispatch(setSelectedDomElements([]));
						activateVisualEditsIfAny();
					}}
					tooltip={iframeReady ? 'Image tool' : 'Prototype code is loading, please wait...'}
					tooltipId="image-tool-tooltip"
				/>
			</>
		);
	};
	return (
		<S.Base $leftDocked={inspectorLeftPanelDocked} $rightDocked={inspectorRightPanelDocked}>
			<S.NavbarWrapper>
				<Navbar>
					<NavbarLeft>
						{scene && <NavbarButton icon={IconType.CLOSE} onClick={exit} />}
						<NavbarButton
							icon={IconType.REFRESH}
							onClick={() => {
								setIframeReloadKey((prev) => prev + 1);
							}}
							disabled={!siteUrl}
							tooltip={
								!siteUrl ? 'Reload is not available for imported images' : 'Reload prototype'
							}
							tooltipId="reload-tooltip"
						/>
						{isEditingNavTitle ? (
							<S.NavTitleInput
								ref={navTitleInputRef}
								value={editedNavTitle}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setEditedNavTitle(e.target.value)
								}
								onBlur={onNavTitleCommit}
								onKeyDown={onNavTitleKeyDown}
							/>
						) : (
							<NavbarTitle>
								<S.NavTitleClickable onClick={onNavTitleClick}>{frame.title}</S.NavTitleClickable>
							</NavbarTitle>
						)}
					</NavbarLeft>

					<NavbarCenter>{renderCenterControls()}</NavbarCenter>

					<NavbarRight>
						{/* Device Preset Buttons */}
						{DEFAULT_DEVICES.map((device) => {
							const isActive =
								prototypeFrameSize.width === device.width &&
								prototypeFrameSize.height === device.height;
							const tooltipId = `device-${device.type}-tooltip`;

							return (
								<NavbarButton
									key={device.type}
									icon={
										device.type === 'mobile'
											? IconType.MOBILE
											: device.type === 'tablet'
											? IconType.TABLET
											: IconType.DESKTOP
									}
									active={isActive}
									disabled={!siteUrl}
									onClick={() => onDevicePreset(device)}
									tooltip={
										!siteUrl
											? 'Device presets are not available for imported images'
											: `Set to ${device.type} (${device.width}×${device.height})`
									}
									tooltipId={tooltipId}
								/>
							);
						})}

						<DropdownMenu menuItems={moreMenuItems}>
							<S.DropdownTrigger
								data-tooltip-id="more-menu-tooltip"
								data-tooltip-content="More options"
							>
								<Icon type={IconType.MORE2} width={16} height={16} />
							</S.DropdownTrigger>
						</DropdownMenu>
						<Tooltip id="more-menu-tooltip" placement="bottom" />
						<S.Share>
							<ExportDropdown>
								<ExportMenuItem
									icon={IconType.GENERATE}
									label="Export for AI"
									description=".MD for Claude Code, ChatGPT, Codex…"
									onSelect={() => {
										trackMiscAction('open_export_frames_ai_modal');
										dispatch(showExportFramesAiModal({ fixedFrameIds: [frame.id] }));
									}}
								/>
								<ExportMenuItem
									icon={IconType.IMAGE}
									label="Export Assets"
									description="PNG, Figma SVG"
									onSelect={() => {
										trackMiscAction('open_export_frames_assets_modal');
										dispatch(showExportFramesAssetsModal({ fixedFrameIds: [frame.id] }));
									}}
								/>
								<ExportMenuItem
									icon={IconType.BRACKET}
									label="Export Code"
									description="React, Next.js, HTML…"
									onSelect={() => {
										trackMiscAction('open_export_frames_code_modal');
										dispatch(showExportFramesCodeModal({ fixedFrameIds: [frame.id] }));
									}}
								/>
							</ExportDropdown>
						</S.Share>
						<S.Share>
							<Button
								onSelected={() => {
									trackMiscAction('open_frame_share_modal');
									setShowShareModal(true);
								}}
								type={ButtonType.NOTICEABLE}
							>
								Share
							</Button>
						</S.Share>
					</NavbarRight>
				</Navbar>
			</S.NavbarWrapper>
			<S.PrototypeArea onClick={onPrototypeAreaClick}>
				<Prototype
					frame={frame}
					siteUrl={siteUrl}
					isDragging={isDragging}
					activeTool={activeTool}
					fullscreenMode={fullscreenMode}
					onSelection={onSelection}
					onSetActiveTool={setActiveTool}
					onSetSelectedElements={(elements) => dispatch(setSelectedDomElements(elements))}
					onIframeReadyChange={setIframeReady}
					onContextMenu={onContextMenu}
					prototypeFrameSize={prototypeFrameSize}
					onSetFrameSize={setPrototypeFrameSize}
					iframeReloadKey={iframeReloadKey}
					activeMode={modePickerData?.currentMode ?? activeMode}
				/>
				{/* Overlay (non-docked) panels — always mounted for exit animation */}
				{!inspectorLeftPanelDocked && (
					<InspectorLeftPanel
						frameId={frameId}
						prototypeWidth={prototypeFrameSize.width}
						overlay
						visible={leftPanelVisible}
						onMouseEnter={onLeftHoverEnter}
						onMouseLeave={onLeftHoverLeave}
						onSiteEntrySelected={onSiteEntrySelected}
					/>
				)}
				{!inspectorRightPanelDocked && (
					<InspectorRightPanel
						frameId={frameId}
						selectionInfo={selectionInfo}
						overlay
						visible={rightPanelVisible}
						onMouseEnter={onRightHoverEnter}
						onMouseLeave={onRightHoverLeave}
					/>
				)}
			</S.PrototypeArea>
			{/* Strips — always visible, toggle dock on click */}
			<S.PanelStrip
				$side="left"
				onMouseEnter={!inspectorLeftPanelDocked ? onLeftHoverEnter : undefined}
				onMouseLeave={!inspectorLeftPanelDocked ? onLeftHoverLeave : undefined}
				onClick={() => {
					if (inspectorLeftPanelDocked) {
						dispatch(setInspectorLeftPanelDocked(false));
						if (leftHoverTimerRef.current) clearTimeout(leftHoverTimerRef.current);
						setLeftPanelHovered(false);
					} else {
						dispatch(setInspectorLeftPanelDocked(true));
					}
				}}
			>
				<Icon
					type={
						inspectorLeftPanelDocked ? IconType.DOUBLE_CHEVRON_LEFT : IconType.DOUBLE_CHEVRON_RIGHT
					}
					width={14}
					height={14}
				/>
			</S.PanelStrip>
			<S.PanelStrip
				$side="right"
				onMouseEnter={!inspectorRightPanelDocked ? onRightHoverEnter : undefined}
				onMouseLeave={!inspectorRightPanelDocked ? onRightHoverLeave : undefined}
				onClick={() => {
					if (inspectorRightPanelDocked) {
						dispatch(setInspectorRightPanelDocked(false));
						if (rightHoverTimerRef.current) clearTimeout(rightHoverTimerRef.current);
						setRightPanelHovered(false);
					} else {
						dispatch(setInspectorRightPanelDocked(true));
					}
				}}
			>
				<Icon
					type={
						inspectorRightPanelDocked ? IconType.DOUBLE_CHEVRON_RIGHT : IconType.DOUBLE_CHEVRON_LEFT
					}
					width={14}
					height={14}
				/>
			</S.PanelStrip>
			<FrameBar frameId={frameId} mode="prototype" onClose={exit} />
			{/* Docked panels render in grid areas */}
			{inspectorLeftPanelDocked && (
				<InspectorLeftPanel
					frameId={frameId}
					prototypeWidth={prototypeFrameSize.width}
					onSiteEntrySelected={onSiteEntrySelected}
				/>
			)}
			{inspectorRightPanelDocked && (
				<InspectorRightPanel frameId={frameId} selectionInfo={selectionInfo} />
			)}
			{showShareModal && (
				<ShareFrameModal frameId={frame.id} onClose={() => setShowShareModal(false)} />
			)}
			{contextMenuOpen && contextMenuPosition && (
				<S.ContextMenuWrapper x={contextMenuPosition.x} y={contextMenuPosition.y} isVisible={true}>
					<S.ContextMenu>
						<S.Items onClick={onExportClick}>Copy selection as SVG</S.Items>
					</S.ContextMenu>
				</S.ContextMenuWrapper>
			)}
		</S.Base>
	);
};

export default FrameInspector;
